import { useState, useEffect, useMemo } from "react";
import {
  CircularProgress,
  Chip,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  Backdrop,
  Switch,
  Snackbar,
  IconButton,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import Categories from "../../components/categories/categories";
import { useDispatch, useSelector } from "react-redux";
import Menu from "../../pages/menu/menu";
import {
  getCategories,
  getPreference,
  setPreference,
  getCategoryPreferences,
  setCategoryPreferences,
} from "../../actions";
import "./user-settings.scss";
import isEmpty from "lodash/isEmpty";
import { sdlConsentAvailable } from "../../components/sdl-consent/sdl-audience";
import SdlStandingConsentSection from "../../components/sdl-consent/sdl-standing-consent-section";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    minHeight: "100vh",
    height: "auto",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

export default function UserSettingsPage() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const categoriesData = useSelector((state) => state.categoriesList);
  const categoryPreferences = useSelector(
    (state) => state.getCategoryPreferences?.getCategoryPreferences
  );
  const userPreferenceData = useSelector(
    (state) => state.getUserPreference?.preference
  );

  const { preference: setUserPreferences, loading: setUserPreferencesLoading } =
    useSelector((state) => state.setUserPreference || {});
  const cPPostData = useSelector((state) => state.setCategoryPreferences);

  const [selectedExamType, setSelectedExamType] = useState([]);

  const [examTypeList, setExamTypeList] = useState([]);

  const [emailPracticeQs, setEmailPracticeQs] = useState(false);
  const [smsNotifications, setSmsNotifications] = useState(true);

  const [originalPreferences, setOriginalPreferences] = useState({
    examType: [],
    emailPracticeQs: emailPracticeQs,
    smsNotifications: true,
  });

  const [open, setOpen] = useState(false);
  const [updateSettingStatus, setUpdateSettingsStatus] = useState(false);
  const [isExamTypeEmpty, setIsExamTypeEmpty] = useState(false);
  const [smsConsentSnackbar, setSmsConsentSnackbar] = useState({ open: false, optIn: false });

  const setUserPreferencesOnFetch = (preferences) => {
    setEmailPracticeQs(preferences?.emailPracticeQuestions);
    setSmsNotifications(preferences?.smsNotifications ?? true);
    setOriginalPreferences((prev) => ({
      ...prev,
      emailPracticeQs: preferences?.emailPracticeQuestions,
      smsNotifications: preferences?.smsNotifications ?? true,
    }));
  };

  useEffect(() => {
    if (categoriesData && Object.keys(categoriesData)?.length) {
      setExamTypeList(categoriesData?.categoriesList?.examType || []);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (userPreferenceData) {
      setUserPreferencesOnFetch(userPreferenceData);
    }
  }, [userPreferenceData]);

  useEffect(() => {
    if (setUserPreferences) {
      setUserPreferencesOnFetch(setUserPreferences);
    }
  }, [setUserPreferences]);

  useEffect(() => {
    if (categoryPreferences && Object.keys(categoryPreferences)?.length) {
      const examTypes = categoryPreferences?.examType || [];
      setSelectedExamType(examTypes);
      setIsExamTypeEmpty(examTypes.length === 0); 

      setOriginalPreferences((prev) => ({
        ...prev,
        examType: [...(categoryPreferences?.examType || [])],
      }));
    }
  }, [categoryPreferences]);

  useEffect(() => {
    if (
      !!Object.keys(cPPostData?.setCategoryPreferences || {}).length &&
      !cPPostData?.loading
    ) {
      const userId = window.localStorage.getItem("userId");
      if (userId) {
        dispatch(getCategoryPreferences(userId));
      }
    }
  }, [cPPostData]);

  const setSnackbarPostSettingUpdate = () => {
    const userPrefCalled =
      !isEmpty(setUserPreferences) || setUserPreferencesLoading === true;
    const catPrefCalled =
      !isEmpty(cPPostData?.setCategoryPreferences) ||
      cPPostData?.loading === true;

    const userPrefDone = !setUserPreferencesLoading;
    const catPrefDone = !cPPostData?.loading;

    const userPrefSuccess =
      setUserPreferences?.emailPracticeQuestions !== undefined;
    const catPrefSuccess = cPPostData?.setCategoryPreferences !== null;

    const allDone =
      (userPrefCalled ? userPrefDone : true) &&
      (catPrefCalled ? catPrefDone : true);

    if (allDone) {
      const allSuccess =
        (userPrefCalled ? userPrefSuccess : true) &&
        (catPrefCalled ? catPrefSuccess : true);

      setUpdateSettingsStatus(allSuccess);
      setOpen(true);
    }
  };
  useEffect(() => {
    if (
      (!isCategoryPreferenceModified() && !isUserPreferenceModified()) ||
      (!Object.keys(cPPostData)?.length &&
      isEmpty(setUserPreferences))
    )
      return false;
    setSnackbarPostSettingUpdate();
  }, [setUserPreferences, setUserPreferencesLoading, cPPostData]);

  useEffect(() => {
    if (
      categoriesData &&
      !Object.keys(categoriesData)?.length &&
      categoriesData.loading === undefined
    ) {
      dispatch(getCategories());
    }

    const userId = window.localStorage.getItem("userId");
    if (userId) {
      if (!categoryPreferences) {
        dispatch(getCategoryPreferences(userId));
      }
      if (!userPreferenceData) {
        dispatch(getPreference(userId));
      }
    }
  }, [dispatch]);

  const getLabelById = (id, list) => {
    const match = list.find((item) => item._id === id || item.id === id);
    return match?.name || id;
  };

  const handleDelete = (id, categoryType) => {
    if (categoryType === "examType") {
      const newExamTypes = [...selectedExamType].filter((item) => item !== id);
      setSelectedExamType(newExamTypes);
      setIsExamTypeEmpty(newExamTypes.length === 0);
    }
  };

  const toggleEmailQs = (evt) => {
    const newMode = evt.target.checked;
    setEmailPracticeQs(newMode);
  };

  const toggleSmsNotifications = (evt) => {
    const checked = evt.target.checked;
    setSmsNotifications(checked);
    setSmsConsentSnackbar({ open: true, optIn: checked });
  };
  const arraysEqual = (arr1, arr2) => {
    const a = [...arr1].sort();
    const b = [...arr2].sort();
    return a.length === b.length && a.every((val, index) => val === b[index]);
  };
  const isCategoryPreferenceModified = () => {
    return !arraysEqual(originalPreferences.examType, selectedExamType);
  };

  const isUserPreferenceModified = () => {
    return (
      originalPreferences.emailPracticeQs !== emailPracticeQs ||
      originalPreferences.smsNotifications !== smsNotifications
    );
  };

  const isSaveEnabled = useMemo(() => {
    return (isCategoryPreferenceModified() || isUserPreferenceModified()) &&
           selectedExamType.length > 0;
  }, [originalPreferences, selectedExamType, emailPracticeQs, smsNotifications]);


  const handleSaveSettings = () => {
    const userId = window.localStorage.getItem("userId");
    if (isCategoryPreferenceModified()) {
      const categoryMap = {
        discipline: [...(categoryPreferences?.discipline || [])],
        organSystem: [...(categoryPreferences?.organSystem || [])],
        examType: [...(selectedExamType || [])],
      };
      dispatch(setCategoryPreferences({ userId, categoryMap }));
    }

    if (isUserPreferenceModified()) {
      const settingsParam = {
        userId,
        body: {
          emailPracticeQuestions: emailPracticeQs,
          smsNotifications: smsNotifications,
        },
      };
      dispatch(setPreference(settingsParam));
    }
  };

  return (
    <>
      <Backdrop
        className={classes.backdrop}
        open={!!cPPostData?.loading || !!setUserPreferencesLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box
          className={`${
            updateSettingStatus ? "success-alert" : "success-warn"
          } save-setting-snackbar`}
        >
          {updateSettingStatus
            ? `Settings saved successfully`
            : `Failed to save settings. Please try again`}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setOpen(false)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
      <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      autoHideDuration={3000}
      open={isExamTypeEmpty}
      onClose={() => setIsExamTypeEmpty(false)}
    >
      <Box className="success-warn save-setting-snackbar">
        Please select at least one exam type
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={() => setIsExamTypeEmpty(false)}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={4000}
        open={smsConsentSnackbar.open}
        onClose={() => setSmsConsentSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Box className={`${smsConsentSnackbar.optIn ? "success-alert" : "success-warn"} save-setting-snackbar`}>
          {smsConsentSnackbar.optIn
            ? "You have opted in to receive SMS messages from QLearn by Medlearnity. Text STOP to opt out. Text HELP for support. Msg & data rates may apply."
            : "You have opted out of QLearn SMS messages."}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSmsConsentSnackbar((prev) => ({ ...prev, open: false }))}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
      <Box className={`${classes.container} settings-container`}>
        <Menu />
        <Box
          sx={{
            display: "flex",
            height: "max-content",
            width: "100%",
            justifyContent: "center",
            padding: "24px",
            flexDirection: "column",
          }}
        >
          <Typography variant="h4">User Settings</Typography>
          {categoriesData?.loading ? (
            <CircularProgress color="inherit" />
          ) : (
            <Paper elevation={2} className="settings-paper-container">
              <Typography variant="h6" gutterBottom>
                Your Selected Preferences
              </Typography>
              <Typography
                variant="subtitle2"
                color="secondary"
                gutterBottom
                className="subtitleStyle"
              >
                Select your default preferences. These will appear in the left
                menu for quick access.
              </Typography>
              <Divider />
              <Box
                sx={{
                  padding: "10px 0px",
                }}
              >
                {examTypeList?.length !== 0 && selectedExamType?.length > 0 && (
                  <>
                    <Typography variant="body2">Exam Type</Typography>
                    <Box className="chipWrapper">
                      {selectedExamType.map((item) => (
                        <Chip
                          key={item}
                          label={getLabelById(item, examTypeList)}
                          onDelete={() => handleDelete(item, "examType")}
                          className="preferenceChips"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>

              <Divider />
              <Box sx={{ mb: 2, mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="secondary"
                  gutterBottom
                  className="subtitleStyle"
                >
                  Select your category preferences below
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Categories
                    onExamTypeCatSelect={(quesArr) => {
                      setSelectedExamType([...quesArr]);
                      setIsExamTypeEmpty(quesArr.length === 0);
                    }}
                    selectedExamType={selectedExamType}
                    examType={examTypeList}
                    isViewDisabled={false}
                    hideCategoryWhenEmpty={true}
                    hidePowerSearch={true}
                  />
                </Box>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography>Email Practice Question:</Typography>
                  <Switch
                    checked={emailPracticeQs}
                    onChange={(e) => toggleEmailQs(e)}
                    name="emailPracticeQuestion"
                    color="primary"
                  />
                </Box>
                <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                  <Typography>SMS Notifications:</Typography>
                  <Switch
                    checked={smsNotifications}
                    onChange={(e) => toggleSmsNotifications(e)}
                    name="smsNotifications"
                    color="primary"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" className="subtitleStyle">
                    By opting in, you consent to receive transactional SMS messages
                    from QLearn by Medlearnity for account verification purposes only.
                    Msg &amp; data rates may apply. Text STOP to opt out. Text HELP
                    for help. Your mobile opt-in data will not be shared with third
                    parties.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sms-disclosure-link"
                    >
                      Privacy Policy
                    </a>
                    {" | "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sms-disclosure-link"
                    >
                      Terms &amp; Conditions
                    </a>
                  </Typography>
                </Box>
                {/* SDL consent surface — DARK: absent from the tree unless the
                    FE flag is on (spec AC 2); server stays authoritative. */}
                {sdlConsentAvailable() && <SdlStandingConsentSection />}
              </Box>

              <Button
                variant="contained"
                color="primary"
                disabled={!isSaveEnabled}
                onClick={handleSaveSettings}
                className="save-settings-btn"
              >
                Save Settings
              </Button>
            </Paper>
          )}
        </Box>
      </Box>
    </>
  );
}
